import React, { useEffect, useRef } from "react";
import { useSocratesInterview } from "../../../hooks/useSocratesInterview";
import Bubble from "../../../components/bubble";

interface SocratesChatContainerProps {
  interviewId: number | null;
}

/**
 * SocratesChatContainer
 * 소크라테스 채팅 메시지를 표시하는 컨테이너
 * - 메시지 목록 표시
 */
const SocratesChatContainer: React.FC<SocratesChatContainerProps> = ({
  interviewId,
}) => {
  const { messages, loading, chatLoading, loadInterview, resetState } =
    useSocratesInterview();

  // 스크롤 참조
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 면접 로드 - interviewId 변경 시 이전 상태 초기화 후 새 면접 로드
  useEffect(() => {
    if (interviewId) {
      // 이전 면접 데이터 초기화 후 새 면접 로드
      resetState();
      loadInterview(interviewId);
    }

    // 컴포넌트 언마운트 시 상태 초기화
    return () => {
      resetState();
    };
  }, [interviewId]); // resetState와 loadInterview는 의존성에서 제외 (무한 루프 방지)

  // 메시지 변경 또는 로딩 상태 변경 시 스크롤
  useEffect(() => {
    // 다음 렌더링 사이클에서 스크롤 실행
    const timer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [messages, chatLoading]);

  // 로딩 중이거나 메시지가 없을 때 로딩 상태 표시
  if (loading && (!messages || messages.length === 0)) {
    return (
      <div className="flex flex-col gap-6 w-full max-w-[800px]">
        <div className="flex justify-start px-4">
          <Bubble sender="agent-so" state="loading" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-[800px] pb-8">
      {/* 메시지 목록 */}
      {messages &&
        messages.map((message: any, index: number) => {
          const isUser = message.role === "user";
          const isAssistant = message.role === "assistant";

          return (
            <div
              key={message.id || index}
              className={`w-full flex px-4 ${
                isUser ? "justify-end" : "justify-start"
              }`}
            >
              {isUser && (
                <Bubble
                  sender="user"
                  content={message.content}
                  state="completed"
                />
              )}
              {isAssistant && (
                <Bubble
                  sender="agent-so"
                  content={message.content}
                  state="completed"
                />
              )}
            </div>
          );
        })}

      {/* 채팅 로딩 중일 때만 로딩 버블 표시 (답변 제출 시에만) */}
      {chatLoading && (
        <div className="flex justify-start px-4">
          <Bubble sender="agent-so" state="loading" />
        </div>
      )}

      {/* 스크롤 앵커 - Footer 높이만큼 여유 공간 확보 */}
      <div ref={messagesEndRef} className="h-8" />
    </div>
  );
};

export default SocratesChatContainer;
